<script lang="ts" setup>
    import { provide, reactive, ref, toRefs, computed } from "vue";
    import { useVuelidate } from "@vuelidate/core";
    import { required } from "@vuelidate/validators";
    import { useEmployeesStore } from "../../../stores/employeesStore";
    import Button from "@/base-components/Button";
    import BaseButton from "@/base-components/Button/BaseButton.vue";
    import Lucide from "@/base-components/Lucide";
    import Modal from "@/base-components/Dialog/Modal.vue";
    import {
        NotificationElement, BaseNotification
    } from "@/base-components/Notification";
    import SubmitButton from "@/base-components/Button/SubmitButton.vue";
    import Tippy from "@/base-components/Tippy/Tippy.vue";
    import BaseAlert from "@/base-components/Alert/BaseAlert.vue";
    import FormFile from "@/base-components/Form/FormFile.vue";

    const emit = defineEmits(["fetchEmployees"]);

    const uploading = ref(false);
    const dialog = ref(false);
    const EmployeeStore = useEmployeesStore();
    const backendValidationErrors = ref({});
    const errorNotification = ref<NotificationElement>();
    const successNotification = ref<NotificationElement>();
    //@ts-ignore
    const $externalResults = ref<Record<string, any> | []>({});
    const inputFile = ref<HTMLInputElement>();
    const fileName = ref<string | undefined>('');

    const uploadFile = (e: Event) => {
        const target = e.target as HTMLInputElement;

        if (!target.files) {
            return;
        }
        fileName.value = target.files?.[0]?.name ?? "";
        form.attachment = target.files[0];
        import_error.value = "";
        import_errors.value = [];
    };

    const form = reactive({
        attachment: null as File | null
    });

    const rules = {
        attachment: {
            required
        }
    };
    //@ts-ignore
    //prettier-ignore
    const validate = useVuelidate(rules, toRefs(form), {$externalResults});

    const open = () => {
        dialog.value = true;
    };

    const close = () => {
        dialog.value = false;
        $externalResults.value = {};
        import_error.value = "";
        import_errors.value = [];
        fileName.value = "";
    };

    const download = async () => {
        await EmployeeStore.downloadImportTemplate();
    };

    const errorNotificationToggle = () => {
        // Show notification
        errorNotification.value?.showToast();
    };
    provide(
        "bind[errorNotification]",
        (el: NotificationElement) => {
            errorNotification.value = el;
        }
    );
    const showErrorsNotification = () => {
        errorNotificationToggle();
    };

    const successNotificationToggle = () => {
        successNotification.value?.showToast();
    };
    provide(
        "bind[successNotification]",
        (el: NotificationElement) => {
            successNotification.value = el;
        }
    );
    const showSuccessNotification = () => {
        successNotificationToggle();
    };

    const import_errors = ref<string[]>([]);
    const import_error = ref<string | null>(null);

    const onClickUpload = async () => {
        uploading.value = true;
        validate.value.$reset();
        validate.value.$clearExternalResults();
        validate.value.$touch();

        if (validate.value.$invalid) {
            backendValidationErrors.value = {
                message: ["Incomplete or Missing required data"]
            };

            showErrorsNotification();
            uploading.value = false;
            return;
        }
        if (!validate.value.$invalid) {
            try {
                const fd = new FormData();
                fd.append(
                    "attachment",
                    form.attachment as File
                );
                await EmployeeStore.importEmployees(fd).then(
                    async (response: any) => {
                        if (response.status === "success") {
                            close();
                            showSuccessNotification();
                            emit("fetchEmployees");
                        } else {
                            // @ts-ignore
                            inputFile.value.value = null;
                            if (response.error?.errors.file) {
                                import_errors.value =
                                    response.error.errors.file;
                            } else if (response.error?.errors) {
                                // if (response.error?.errors['attachment'][0]) {
                                //     import_errors.value = response.error?.errors['attachment'];
                                // }
                                if (
                                    response.error.errors.errors
                                ) {
                                    import_errors.value =
                                        response.error.errors.errors;
                                } else {
                                    import_errors.value =
                                        response.error?.errors[
                                            "attachment"
                                        ];
                                }
                            } else if (response.error.error) {
                                import_error.value =
                                    response.error.error;
                            }
                            uploading.value = false;
                        }
                    }
                );
            } catch (error: any) {
                // @ts-ignore
                inputFile.value.value = null;
                form.attachment = null;
                $externalResults.value =
                    error?.response?.data?.errors;
                validate.value.$reset();
            } finally {
                uploading.value = false;
            }
        }
    };
    const openInputHandler = () => {
        inputFile.value?.click();
    };
</script>

<template>
    <Modal :open="dialog" size="xl">
        <template #trigger>
            <BaseButton
                variant="success"
                kind="icon"
                @click="open"
                icon="Import"
                tippy-content="Import Employees"
            ></BaseButton>
        </template>

        <template #content>
            <BaseAlert variant="secondary">
                <div>
                    <span class="sr-only">Info</span>
                    <div class="ml-2">
                        <span class="font-medium">
                            Please be informed that certain fields are not
                            updated through the import process. These fields
                            include:
                        </span>
                        <ul class="mt-2 grid list-inside list-disc grid-cols-2">
                            <li>Employee Status</li>
                            <li>Employee Cost</li>
                            <li>RE Fees</li>
                            <li>Nonrefundable Amount</li>
                            <li>
                                Import doesn't support terminations/suspensions
                            </li>
                        </ul>
                        <div class="mb-2 mt-2">
                            These values are set only during the creation
                            process and are not modified by imports. To ensure
                            accurate data, kindly review and update these fields
                            manually when necessary.
                        </div>
                        <span class="mt-2 font-medium">
                            The following fields should follow the validations
                            below:
                        </span>
                        <ul class="mt-2 list-inside list-disc">
                            <li>Phone numbers must be numeric</li>
                            <li>Amounts such as Base Pay must be numeric</li>
                            <li>
                                Shift should be set as
                                <span class="font-medium">
                                    Mid Shift, Day Shift, Night Shift
                                </span>
                            </li>
                            <li>
                                Timezone must be in
                                <span class="font-medium">Asia/Manila</span>
                                format
                            </li>
                            <!-- <li>Dates must be in <span class="font-medium">mm/dd/YYYY</span> format (e.g. 09/18/2024).</li> -->
                            <li>
                                Primary location and secondary location must be
                                <span class="font-medium">
                                    wfo, wfh or hybrid
                                </span>
                            </li>
                            <li>
                                Leave, holiday and occurrence policy should be
                                the name of the existing policies in the system
                            </li>
                            <li>
                                Leave and occurrence policy should also be
                                already assigned to the employee's client, if
                                not it will not take effect
                            </li>
                            <li>
                                When no holiday, leave, or occurrence policy is
                                provided, the system will use the default client
                                policy
                            </li>
                            <li>
                                Policy start dates can be empty for first time
                                creation / policy assignment. Current date will
                                be used if not provided.
                            </li>
                            <li>
                                Policy start dates must be future dates for
                                assigning future policies. Past dates will not
                                be accepted.
                            </li>
                            <li>
                                Holiday policy start date must always be set to
                                January 1st
                            </li>
                            <li class="font-bold">
                                Import will fail if any rows have errors. When
                                that happens, please fix the errors and try
                                again.
                            </li>
                        </ul>
                        <div class="mt-2">
                            Thank you for your attention to this matter.
                        </div>
                    </div>
                </div>
            </BaseAlert>

            <div class="mt-5 flex items-center">
                <h2 class="mr-auto text-base font-medium">Select File</h2>
            </div>

            <BaseAlert variant="danger" v-if="import_error" class="mt-5">
                <div>
                    <h5 class="font-semibold">Something went wrong.</h5>
                    <ul class="list-disc p-4">
                        <li>
                            {{ import_error }}
                        </li>
                    </ul>
                </div>
            </BaseAlert>

            <BaseAlert
                variant="danger"
                v-if="import_errors.length > 0"
                class="mt-5"
            >
                <div>
                    <h5 class="font-semibold">Something went wrong.</h5>
                    <ul class="list-disc p-4">
                        <li
                            v-for="(
                            error, index
                        ) in import_errors"
                            :key="index"
                            class="mb-1"
                        >
                            <span
                                v-if="error.includes('Row')"
                                class="font-semibold text-blue-600"
                            >
                                {{ error.split(':')[0] }}:
                            </span>
                            <span v-else>{{ error }}</span>
                            <span v-if="error.includes('Row')" class="ml-2">
                                {{ error.split(':').slice(1).join(':').trim() }}
                            </span>
                        </li>
                    </ul>
                </div>
            </BaseAlert>

            <!-- <div
                v-if="$externalResults?.length > 0"
                class="px-4 mt-4 text-red-600 bg-red-200 rounded"
            >
                <h5 class="pt-4 font-semibold">Something went wrong.</h5>
                <ul class="p-4 list-disc">
                    <li v-for="error in $externalResults">
                        {{ error[0] }}
                    </li>
                </ul>
            </div> -->

            <div class="mt-4">
                <input
                    ref="inputFile"
                    id="uploadFileBtn"
                    class="btn mr-2 hidden shadow-md"
                    type="file"
                    @change="uploadFile"
                />
                <FormFile
                    @click="openInputHandler"
                    :fileName="fileName"
                />

                <template v-if="validate.attachment.$error">
                    <div
                        v-for="(error, index) in validate
                            .attachment.$errors"
                        :key="index"
                        class="mt-2 text-danger"
                    >
                        {{ error.$message }}
                    </div>
                </template>
            </div>
        </template>

        <template #action>
            <div class="flex flex-col justify-between gap-2 sm:flex-row">
                <div>
                    <Button
                        class="w-full sm:w-auto"
                        type="button"
                        variant="outline-secondary"
                        @click="download"
                    >
                        Download Employee Data
                    </Button>
                </div>

                <div class="flex flex-col justify-end gap-2 sm:flex-row">
                    <Button
                        class="w-full sm:w-24"
                        type="button"
                        variant="outline-secondary"
                        @click="close"
                    >
                        Close
                    </Button>

                    <BaseButton
                        id="btn_process"
                        variant="primary"
                        type="submit"
                        processing-label="Importing..."
                        :is-processing="uploading"
                        :disabled="uploading"
                        @click="onClickUpload"
                        label="Import File"
                        icon="Upload"
                    ></BaseButton>
                </div>
            </div>
        </template>
    </Modal>

    <BaseNotification refKey="successNotification">
        Employees has been imported successfully.
    </BaseNotification>

    <BaseNotification type="error" refKey="errorNotification">
        <ul class="list-inside list-disc">
            <template v-for="errorBag in backendValidationErrors">
                <li>
                    {{ errorBag[0] }}
                </li>
            </template>
        </ul>
    </BaseNotification>
</template>

<style scoped></style>
